// There are lots of properties for each task in project management. What if you want to some of the most important metric to the asset-level so that you can see the most important details on the asset-settings, dashboards and BI? This is what asset mapping allows us to do. We can map properties associated with any activity in project management to an existing asset custom field and see the field value there.
// This linking can happen at both schedule level in the settings page or at the activity level from the context menu on the top-right of activity details.
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { useProjectManagementStore } from '~/project-management/store/pm.store';

export function useAssetMapping() {
  const { $t, common_store } = useCommonImports();
  const project_management_store = useProjectManagementStore();
  const { active_schedule } = storeToRefs(project_management_store);
  const { assets_custom_fields } = storeToRefs(common_store);

  // The key is the field type in project management and the values in the array are the asset custom field types that are eligible to be mapped.
  // Example: planned_start (Planned start) which is a date field in project management can be linked to any of the existing asset custom field types: datetime, date_range and date.
  const pm_to_asset_field_type_map = {
    Text: ['text'],
    Date: ['datetime', 'date_range', 'date'],
    Number: ['number', 'planned_actual'],
    Money: ['number', 'money', 'planned_actual'],
    Percentage: ['number', 'percentage', 'planned_actual'],
    Member: ['members'],
  };

  const pm_activity_fields = computed(() => {
    return [
      ...(active_schedule.value.baseline && active_schedule.value.baselines?.length === 0
        ? [['bl_start', 'Baseline start', 'Date'], ['bl_finish', 'Baseline finish', 'Date']]
        : []),
      ...(active_schedule.value.baselines?.flatMap?.(baseline => [
        [`baseline::${baseline.uid}::start_date`, ` ${baseline.name} - ${$t('Baseline start')}`, 'Date'],
        [`baseline::${baseline.uid}::end_date`, ` ${baseline.name} - ${$t('Baseline finish')}`, 'Date'],
      ]) || []),
      ['planned_start', 'Planned start', 'Date'],
      ['planned_finish', 'Planned finish', 'Date'],
      ['actual_start', 'Actual start', 'Date'],
      ['actual_finish', 'Actual finish', 'Date'],
      ['start', 'Start', 'Date'],
      ['finish', 'Finish', 'Date'],
      ...(active_schedule.value.baseline && active_schedule.value.baselines?.length === 0
        ? [['bl_duration', 'Baseline duration', 'Number']]
        : []),
      ...(active_schedule.value.baselines?.map?.(baseline => [`baseline::${baseline.uid}::duration`, ` ${baseline.name} - ${$t('Baseline duration')}`, 'Number']) || []),
      ['planned_duration', 'Planned duration', 'Number'],
      ['actual_duration', 'Actual duration', 'Number'],
      ['duration', 'Duration', 'Number'],
      ['remaining_duration', 'Remaining duration', 'Number'],
      ['percent_schedule_complete', 'Scheduled progress', 'Percentage'],
      ['progress', 'Progress', 'Percentage'],
      ['state', 'Status', 'Text'],
      ['priority_label', 'Priority', 'Text'],
      ['category_label', 'Category', 'Text'],
      ['tags_labels', 'Tags', 'Text'],
      ['resources_label', 'Resources', 'Text'],
      ['accountable_label', 'Accountable', 'Text'],
      ['spi', 'SPI', 'Percentage'],
      ['cpi', 'CPI', 'Percentage'],
      ['planned_cost', 'Planned cost', 'Money'],
      ['actual_cost', 'Actual cost', 'Money'],
      ['is_critical_label', 'Critical', 'Text'],
      ['total_slack', 'Total slack', 'Number'],
      ['free_slack', 'Free slack', 'Number'],
      ['planned_work', 'Total work', 'Number'],
      ['actual_work', 'Actual work', 'Number'],
      ['remaining_work', 'Remaining work', 'Number'],
      ['planned_work_rate', 'Planned work rate', 'Percentage'],
      ['actual_work_rate', 'Actual work rate', 'Percentage'],
      ['remaining_work_rate', 'Remaining work rate', 'Percentage'],
      ['planned_value', 'Planned value', 'Number'],
      ['earned_value', 'Earned value', 'Number'],
      ['schedule_variance', 'Schedule variance', 'Number'],
      ['cost_variance', 'Cost variance', 'Number'],
      ...Object.keys(active_schedule.value.custom_fields).map((field_name) => {
        return [`custom_field__${field_name}`, field_name, active_schedule.value.custom_fields[field_name].type.charAt(0).toUpperCase() + active_schedule.value.custom_fields[field_name].type.slice(1)];
      }),
    ].map((item) => {
      return {
        value: item[0],
        label: item[0].startsWith('custom_field__') || item[0].startsWith('baseline::') ? item[1] : $t(item[1]),
        type: item[2],
      };
    });
  });

  const all_asset_fields = computed(() => {
    const field_mappings = active_schedule.value.asset_mapping?.configs?.map?.((config) => {
      const base_uid = config.field_uid.replace(/__(planned|actual|start|end)$/, '');
      const item = assets_custom_fields.value.find(item => item.uid === base_uid);

      if (item) {
        let label = item.name;
        if (config.field_uid.includes('__planned'))
          label += ` [${$t('Planned')}]`;
        else if (config.field_uid.includes('__actual'))
          label += ` [${$t('Actual')}]`;
        else if (config.field_uid.includes('__start'))
          label += ` [${$t('Start')}]`;
        else if (config.field_uid.includes('__end'))
          label += ` [${$t('End')}]`;

        return {
          ...item,
          uid: config.field_uid,
          label,
          value: label,
        };
      }
      else {
        return false;
      }
    }).filter(Boolean);

    const custom_fields_not_in_configs = assets_custom_fields.value.filter((item) => {
      const related_mappings = field_mappings?.filter?.(mapping => mapping.uid.startsWith(item.uid));
      if (item.type === 'planned_actual' || item.type === 'date_range')
        return related_mappings?.length < 2 || related_mappings === undefined;
      else
        return related_mappings?.length === 0 || related_mappings === undefined;
    });

    const custom_fields_with_suffixes = custom_fields_not_in_configs.flatMap((item) => {
      if (item.type === 'planned_actual') {
        const planned_item = {
          ...item,
          uid: `${item.uid}__planned`,
          label: `${item.name} [${$t('Planned')}]`,
          value: `${item.name} [${$t('Planned')}]`,
        };

        const actual_item = {
          ...item,
          uid: `${item.uid}__actual`,
          label: `${item.name} [${$t('Actual')}]`,
          value: `${item.name} [${$t('Actual')}]`,
        };

        return [planned_item, actual_item];
      }

      if (item.type === 'date_range') {
        const start_item = {
          ...item,
          uid: `${item.uid}__start`,
          label: `${item.name} [${$t('Start')}]`,
        };

        const end_item = {
          ...item,
          uid: `${item.uid}__end`,
          label: `${item.name} [${$t('End')}]`,
        };

        return [start_item, end_item];
      }

      return {
        label: item.name,
        value: item.name,
        ...item,
      };
    });

    return [
      ...(Array.isArray(field_mappings) ? field_mappings : []),
      ...custom_fields_with_suffixes,
    ];
  });

  // There are a few limitations to what asset fields can be mapped to a PM property. Along with field type, there are several other constraints. The particular asset field shouldn't have been already mapped somewhere else in the application.
  // Along with that, there are a few more cases. Asset field types like date_range and planned_actual work in pairs. start_date can be linked to one field and the end date to another. planned to one and actual to another, etc.
  function getPossibleAssetFields(mappings_list, selected_asset_fields, selected_asset_fields_of_other_tasks_or_schedules) {
    return all_asset_fields.value.map((_item, index) => {
      const type_of_property = pm_activity_fields.value.find(property => property.value === mappings_list?.[index]?.properties)?.type;
      return all_asset_fields.value.filter(
        option =>
          !selected_asset_fields.includes(option.uid)
          || option.uid === selected_asset_fields[index],
      ).filter(
        option => pm_to_asset_field_type_map[type_of_property]?.includes?.(option.type),
      ).filter(
        option => !selected_asset_fields_of_other_tasks_or_schedules.includes(option.uid),
      );
    });
  }

  return {
    pm_to_asset_field_type_map,
    pm_activity_fields,
    all_asset_fields,
    getPossibleAssetFields,
  };
}
